import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  BookOpen, Plus, Search, FileText, Award, GraduationCap, Microscope,
  Users, CheckCircle, XCircle, Clock, Filter, Trash2, Edit, Eye,
  TrendingUp, Building, ChevronDown, CalendarDays, Lightbulb
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE as API_URL } from '../lib/apiBase';
const CATEGORIES = [
  "Journal Paper Published", "Conference Paper Presented", "Conference/Seminar Attended",
  "Faculty Development Program (FDP)", "Guest Lecture / Invited Talk",
  "Session Chair / Keynote Speaker", "Workshop Conducted", "Workshop Attended",
  "Book / Chapter Published", "Patent Filed / Granted", "Online Certification / Course", "Custom"
];

const getCategoryIcon = (cat) => {
  if (cat?.includes('Journal') || cat?.includes('Book')) return <FileText className="w-4 h-4" />;
  if (cat?.includes('Conference') || cat?.includes('Seminar')) return <Users className="w-4 h-4" />;
  if (cat?.includes('FDP') || cat?.includes('Workshop') || cat?.includes('Certification')) return <GraduationCap className="w-4 h-4" />;
  if (cat?.includes('Guest') || cat?.includes('Session') || cat?.includes('Keynote')) return <Award className="w-4 h-4" />;
  if (cat?.includes('Patent')) return <Microscope className="w-4 h-4" />;
  return <Lightbulb className="w-4 h-4" />;
};

const getCategoryColor = (cat) => {
  if (cat?.includes('Journal') || cat?.includes('Book')) return 'bg-blue-100 text-blue-700';
  if (cat?.includes('Conference') || cat?.includes('Seminar')) return 'bg-purple-100 text-purple-700';
  if (cat?.includes('FDP') || cat?.includes('Workshop') || cat?.includes('Certification')) return 'bg-green-100 text-green-700';
  if (cat?.includes('Guest') || cat?.includes('Session') || cat?.includes('Keynote')) return 'bg-amber-100 text-amber-700';
  if (cat?.includes('Patent')) return 'bg-red-100 text-red-700';
  return 'bg-slate-100 text-slate-700';
};

function h() {
  const token = localStorage.getItem('hrms-token');
  return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
}


/* ====================================================================
   ADD / EDIT ACTIVITY DIALOG
   ==================================================================== */
function ActivityDialog({ open, onClose, onDone, activity = null }) {
  const isEdit = !!activity;
  const [form, setForm] = useState({
    category: '', custom_category: '', title: '', description: '', date: '',
    end_date: '', journal_name: '', issn: '', impact_factor: '', indexing: '',
    doi: '', event_name: '', organizer: '', location: '', role: '',
    publisher: '', isbn: '', patent_number: '', patent_status: '',
    duration_days: '', certificate_url: '', attachment_name: '', attachment_data: '', attachment_type: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (activity) {
      setForm({ ...form, ...activity, impact_factor: activity.impact_factor?.toString() || '', duration_days: activity.duration_days?.toString() || '' });
    } else {
      setForm({ category: '', custom_category: '', title: '', description: '', date: '',
        end_date: '', journal_name: '', issn: '', impact_factor: '', indexing: '',
        doi: '', event_name: '', organizer: '', location: '', role: '',
        publisher: '', isbn: '', patent_number: '', patent_status: '',
        duration_days: '', certificate_url: '', attachment_name: '', attachment_data: '', attachment_type: '' });
    }
  }, [activity, open]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File too large (max 5MB)'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      set('attachment_data', reader.result);
      set('attachment_name', file.name);
      set('attachment_type', file.type);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!form.category || !form.title || !form.date) { toast.error('Category, title, and date are required'); return; }
    setSaving(true);
    try {
      const body = { ...form };
      body.impact_factor = body.impact_factor ? parseFloat(body.impact_factor) : null;
      body.duration_days = body.duration_days ? parseInt(body.duration_days) : null;
      Object.keys(body).forEach(k => { if (body[k] === '' || body[k] === null) delete body[k]; });

      const url = isEdit ? `${API_URL}/api/lnd/activities/${activity.activity_id}` : `${API_URL}/api/lnd/activities`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: h(), body: JSON.stringify(body), credentials: 'include' });
      if (res.ok) {
        toast.success(isEdit ? 'Activity updated' : 'Activity added');
        onDone();
        onClose();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Failed');
      }
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const cat = form.category;
  const showPublication = cat?.includes('Journal') || cat?.includes('Book');
  const showConference = cat?.includes('Conference') || cat?.includes('Seminar') || cat?.includes('Guest') || cat?.includes('Session') || cat?.includes('Keynote');
  const showPatent = cat?.includes('Patent');
  const showDuration = cat?.includes('FDP') || cat?.includes('Workshop') || cat?.includes('Certification');

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="lnd-activity-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-blue-600" /> {isEdit ? 'Edit Activity' : 'Add New Activity'}</DialogTitle>
          <DialogDescription>Record your professional development activities</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {/* Category + Title */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => set('category', v)}>
                <SelectTrigger data-testid="lnd-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.category === 'Custom' && (
              <div className="space-y-1.5">
                <Label>Custom Category Name</Label>
                <Input value={form.custom_category} onChange={(e) => set('custom_category', e.target.value)} placeholder="e.g., Industry Consultancy" data-testid="lnd-custom-category" />
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Title *</Label>
            <Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g., Machine Learning in Education..." data-testid="lnd-title" />
          </div>

          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Brief description..." rows={2} data-testid="lnd-description" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Date *</Label>
              <Input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} data-testid="lnd-date" />
            </div>
            <div className="space-y-1.5">
              <Label>End Date</Label>
              <Input type="date" value={form.end_date} onChange={(e) => set('end_date', e.target.value)} data-testid="lnd-end-date" />
            </div>
          </div>

          {/* Publication Fields */}
          {showPublication && (
            <div className="p-3 bg-blue-50 rounded-lg space-y-3">
              <p className="text-xs font-semibold text-blue-700">Publication Details</p>
              <div className="grid grid-cols-2 gap-3">
                <Input value={form.journal_name} onChange={(e) => set('journal_name', e.target.value)} placeholder="Journal / Publisher Name" data-testid="lnd-journal" />
                <Input value={form.issn} onChange={(e) => set('issn', e.target.value)} placeholder="ISSN / ISBN" data-testid="lnd-issn" />
                <Input type="number" step="0.1" value={form.impact_factor} onChange={(e) => set('impact_factor', e.target.value)} placeholder="Impact Factor" data-testid="lnd-impact-factor" />
                <Select value={form.indexing || ''} onValueChange={(v) => set('indexing', v)}>
                  <SelectTrigger data-testid="lnd-indexing"><SelectValue placeholder="Indexing" /></SelectTrigger>
                  <SelectContent>
                    {['Scopus', 'SCI', 'UGC', 'Web of Science', 'IEEE', 'Other'].map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Input value={form.doi} onChange={(e) => set('doi', e.target.value)} placeholder="DOI (e.g., 10.1000/xyz123)" data-testid="lnd-doi" />
            </div>
          )}

          {/* Conference/Event Fields */}
          {showConference && (
            <div className="p-3 bg-purple-50 rounded-lg space-y-3">
              <p className="text-xs font-semibold text-purple-700">Event Details</p>
              <div className="grid grid-cols-2 gap-3">
                <Input value={form.event_name} onChange={(e) => set('event_name', e.target.value)} placeholder="Event / Conference Name" data-testid="lnd-event-name" />
                <Input value={form.organizer} onChange={(e) => set('organizer', e.target.value)} placeholder="Organizer" data-testid="lnd-organizer" />
                <Input value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="Location / City" data-testid="lnd-location" />
                <Select value={form.role || ''} onValueChange={(v) => set('role', v)}>
                  <SelectTrigger data-testid="lnd-role"><SelectValue placeholder="Your Role" /></SelectTrigger>
                  <SelectContent>
                    {['Presenter', 'Attendee', 'Session Chair', 'Keynote Speaker', 'Panelist', 'Organizer', 'Invited Speaker'].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Patent Fields */}
          {showPatent && (
            <div className="p-3 bg-red-50 rounded-lg space-y-3">
              <p className="text-xs font-semibold text-red-700">Patent Details</p>
              <div className="grid grid-cols-2 gap-3">
                <Input value={form.patent_number} onChange={(e) => set('patent_number', e.target.value)} placeholder="Patent Number" data-testid="lnd-patent-number" />
                <Select value={form.patent_status || ''} onValueChange={(v) => set('patent_status', v)}>
                  <SelectTrigger data-testid="lnd-patent-status"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    {['Filed', 'Published', 'Granted'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Duration Fields */}
          {showDuration && (
            <div className="p-3 bg-green-50 rounded-lg space-y-3">
              <p className="text-xs font-semibold text-green-700">Program Details</p>
              <div className="grid grid-cols-2 gap-3">
                <Input type="number" value={form.duration_days} onChange={(e) => set('duration_days', e.target.value)} placeholder="Duration (days)" data-testid="lnd-duration" />
                <Input value={form.organizer} onChange={(e) => set('organizer', e.target.value)} placeholder="Organizing Body" data-testid="lnd-fdp-organizer" />
              </div>
              <Input value={form.certificate_url} onChange={(e) => set('certificate_url', e.target.value)} placeholder="Certificate URL (optional)" data-testid="lnd-cert-url" />
            </div>
          )}

          {/* Attachment */}
          <div className="space-y-1.5">
            <Label>Supporting Document / Certificate</Label>
            <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleFileUpload} data-testid="lnd-file-upload" />
            {form.attachment_name && <p className="text-xs text-slate-500">Attached: {form.attachment_name}</p>}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="lnd-save-btn">
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Add Activity'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/* ====================================================================
   ACTIVITY DETAIL DIALOG
   ==================================================================== */
function ActivityDetailDialog({ activity, open, onClose }) {
  if (!activity) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="lnd-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{getCategoryIcon(activity.category)} {activity.title}</DialogTitle>
          <DialogDescription>
            <Badge className={getCategoryColor(activity.category)}>{activity.custom_category || activity.category}</Badge>
            <Badge variant="outline" className="ml-2">{activity.date}</Badge>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2 text-sm">
          {activity.description && <p className="text-slate-600">{activity.description}</p>}
          {activity.journal_name && <div><span className="font-medium">Journal:</span> {activity.journal_name}</div>}
          {activity.issn && <div><span className="font-medium">ISSN/ISBN:</span> {activity.issn}</div>}
          {activity.impact_factor && <div><span className="font-medium">Impact Factor:</span> {activity.impact_factor}</div>}
          {activity.indexing && <div><span className="font-medium">Indexing:</span> <Badge variant="outline">{activity.indexing}</Badge></div>}
          {activity.doi && <div><span className="font-medium">DOI:</span> {activity.doi}</div>}
          {activity.event_name && <div><span className="font-medium">Event:</span> {activity.event_name}</div>}
          {activity.organizer && <div><span className="font-medium">Organizer:</span> {activity.organizer}</div>}
          {activity.location && <div><span className="font-medium">Location:</span> {activity.location}</div>}
          {activity.role && <div><span className="font-medium">Role:</span> <Badge variant="outline">{activity.role}</Badge></div>}
          {activity.patent_number && <div><span className="font-medium">Patent #:</span> {activity.patent_number} ({activity.patent_status})</div>}
          {activity.duration_days && <div><span className="font-medium">Duration:</span> {activity.duration_days} days</div>}
          {activity.certificate_url && <div><span className="font-medium">Certificate:</span> <a href={activity.certificate_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">View</a></div>}
          {activity.employee_name && <div><span className="font-medium">Employee:</span> {activity.employee_name} ({activity.department})</div>}
          <div className="pt-2 border-t">
            <span className="font-medium">Status: </span>
            <Badge className={activity.status === 'verified' ? 'bg-green-100 text-green-700' : activity.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}>
              {activity.status}
            </Badge>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/* ====================================================================
   MY ACTIVITIES TAB (Employee view)
   ==================================================================== */
function MyActivities() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editActivity, setEditActivity] = useState(null);
  const [detailActivity, setDetailActivity] = useState(null);
  const [filterCat, setFilterCat] = useState('all');
  const [filterYear, setFilterYear] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [apiScore, setApiScore] = useState(null);
  const [ugcScore, setUgcScore] = useState(null);

  useEffect(() => { fetchActivities(); }, []);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/lnd/activities`, { headers: h(), credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setActivities(data);
        if (data.length > 0) {
          fetchApiScore(data[0]?.employee_id);
          fetchUgcScore(data[0]?.employee_id);
        }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchApiScore = async (empId) => {
    if (!empId) return;
    try {
      const res = await fetch(`${API_URL}/api/lnd/api-score/${empId}`, { headers: h(), credentials: 'include' });
      if (res.ok) setApiScore(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchUgcScore = async (empId) => {
    if (!empId) return;
    try {
      const res = await fetch(`${API_URL}/api/lnd/ugc-score/${empId}`, { headers: h(), credentials: 'include' });
      if (res.ok) setUgcScore(await res.json());
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this activity?')) return;
    try {
      const res = await fetch(`${API_URL}/api/lnd/activities/${id}`, { method: 'DELETE', headers: h(), credentials: 'include' });
      if (res.ok) { toast.success('Deleted'); fetchActivities(); }
      else toast.error('Failed to delete');
    } catch (e) { toast.error('Failed'); }
  };

  const years = [...new Set(activities.map(a => a.date?.slice(0, 4)).filter(Boolean))].sort().reverse();

  const filtered = activities.filter(a => {
    const matchCat = filterCat === 'all' || a.category === filterCat;
    const matchYear = filterYear === 'all' || a.date?.startsWith(filterYear);
    const matchSearch = !searchTerm || a.title?.toLowerCase().includes(searchTerm.toLowerCase()) || a.category?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchCat && matchYear && matchSearch;
  });

  // Stats
  const stats = {
    total: activities.length,
    verified: activities.filter(a => a.status === 'verified').length,
    pending: activities.filter(a => a.status === 'submitted').length,
    papers: activities.filter(a => a.category?.includes('Journal') || a.category?.includes('Conference Paper')).length,
  };

  return (
    <div className="space-y-6" data-testid="my-activities-tab">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Activities', value: stats.total, icon: BookOpen, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Verified', value: stats.verified, icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Pending', value: stats.pending, icon: Clock, color: 'text-orange-600', bg: 'bg-orange-50' },
          { label: 'Publications', value: stats.papers, icon: FileText, color: 'text-purple-600', bg: 'bg-purple-50' },
        ].map(s => (
          <Card key={s.label} className="border-0 shadow-sm">
            <CardContent className="p-3 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center`}>
                <s.icon className={`w-5 h-5 ${s.color}`} />
              </div>
              <div>
                <p className="text-xs text-slate-500">{s.label}</p>
                <p className="text-lg font-bold">{s.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* AICTE API Score */}
      {apiScore && apiScore.total_api_score > 0 && (
        <Card className="border-0 shadow-sm bg-gradient-to-r from-indigo-50 to-blue-50" data-testid="aicte-api-score-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-indigo-600" />
                <span className="font-semibold text-indigo-800">AICTE API Score</span>
              </div>
              <span className="text-2xl font-bold text-indigo-700" data-testid="api-score-total">{apiScore.total_api_score}</span>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-white/70 rounded-lg p-2">
                <p className="text-sm font-bold text-blue-700">{apiScore.category_1_research}</p>
                <p className="text-xs text-slate-500">Cat I: Research</p>
              </div>
              <div className="bg-white/70 rounded-lg p-2">
                <p className="text-sm font-bold text-green-700">{apiScore.category_2_teaching}</p>
                <p className="text-xs text-slate-500">Cat II: Teaching</p>
              </div>
              <div className="bg-white/70 rounded-lg p-2">
                <p className="text-sm font-bold text-amber-700">{apiScore.category_3_service}</p>
                <p className="text-xs text-slate-500">Cat III: Service</p>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-2 text-center">Based on {apiScore.verified_activities} verified activities</p>
          </CardContent>
        </Card>
      )}

      {/* UGC API Score */}
      {ugcScore && ugcScore.total_ugc_score > 0 && (
        <Card className="border-0 shadow-sm bg-gradient-to-r from-emerald-50 to-teal-50" data-testid="ugc-api-score-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-emerald-600" />
                <span className="font-semibold text-emerald-800">UGC API Score</span>
              </div>
              <span className="text-2xl font-bold text-emerald-700" data-testid="ugc-score-total">{ugcScore.total_ugc_score}</span>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center mb-2">
              <div className="bg-white/70 rounded-lg p-2">
                <p className="text-sm font-bold text-teal-700">{ugcScore.category_1_teaching}</p>
                <p className="text-xs text-slate-500">Cat I: Teaching</p>
                <p className="text-[10px] text-slate-400">max 120</p>
              </div>
              <div className="bg-white/70 rounded-lg p-2">
                <p className="text-sm font-bold text-cyan-700">{ugcScore.category_2_professional}</p>
                <p className="text-xs text-slate-500">Cat II: Professional</p>
                <p className="text-[10px] text-slate-400">max 50</p>
              </div>
              <div className="bg-white/70 rounded-lg p-2">
                <p className="text-sm font-bold text-emerald-700">{ugcScore.category_3_research}</p>
                <p className="text-xs text-slate-500">Cat III: Research</p>
                <p className="text-[10px] text-slate-400">sub-capped</p>
              </div>
            </div>
            {/* Cat III Sub-breakdown */}
            <div className="grid grid-cols-4 gap-2 text-center">
              {[
                { label: 'Pubs', value: ugcScore.category_3_breakdown?.publications, cap: '55%' },
                { label: 'Projects', value: ugcScore.category_3_breakdown?.projects, cap: '20%' },
                { label: 'Guidance', value: ugcScore.category_3_breakdown?.guidance, cap: '10%' },
                { label: 'Training', value: ugcScore.category_3_breakdown?.training_patents, cap: '15%' },
              ].map(s => (
                <div key={s.label} className="bg-white/50 rounded p-1.5">
                  <p className="text-xs font-bold text-slate-700">{s.value || 0}</p>
                  <p className="text-[10px] text-slate-400">{s.label} ({s.cap})</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-2 text-center">Based on {ugcScore.verified_activities} verified activities</p>
          </CardContent>
        </Card>
      )}

      {/* Actions + Filters */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <Button onClick={() => setAddOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-activity-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add Activity
        </Button>
        <div className="flex-1" />
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search..." className="pl-10 w-48" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="lnd-search" />
        </div>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-52" data-testid="lnd-filter-cat"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterYear} onValueChange={setFilterYear}>
          <SelectTrigger className="w-28" data-testid="lnd-filter-year"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Years</SelectItem>
            {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Activity List */}
      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed border-2"><CardContent className="p-12 text-center">
          <BookOpen className="w-16 h-16 mx-auto text-slate-200 mb-3" />
          <h3 className="text-lg font-semibold text-slate-600">No activities yet</h3>
          <p className="text-sm text-slate-400 mt-1">Click "Add Activity" to record your professional development</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(a => (
            <Card key={a.activity_id} className="hover:shadow-md transition-shadow" data-testid={`activity-card-${a.activity_id}`}>
              <CardContent className="p-4 flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${getCategoryColor(a.category)}`}>
                  {getCategoryIcon(a.category)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h4 className="font-medium text-slate-800 text-sm line-clamp-1">{a.title}</h4>
                      <p className="text-xs text-slate-500 mt-0.5">
                        <Badge variant="outline" className="text-xs mr-1">{a.custom_category || a.category}</Badge>
                        <span>{a.date}</span>
                        {a.journal_name && <span> | {a.journal_name}</span>}
                        {a.event_name && <span> | {a.event_name}</span>}
                        {a.indexing && <span> | <Badge className={getCategoryColor(a.category)} variant="outline">{a.indexing}</Badge></span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Badge className={a.status === 'verified' ? 'bg-green-100 text-green-700' : a.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}>
                        {a.status}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="icon" className="w-8 h-8" onClick={() => setDetailActivity(a)} data-testid={`view-${a.activity_id}`}><Eye className="w-3.5 h-3.5" /></Button>
                  {a.status === 'submitted' && (
                    <>
                      <Button variant="ghost" size="icon" className="w-8 h-8" onClick={() => setEditActivity(a)} data-testid={`edit-${a.activity_id}`}><Edit className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="w-8 h-8 text-red-500" onClick={() => handleDelete(a.activity_id)} data-testid={`delete-${a.activity_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ActivityDialog open={addOpen} onClose={() => setAddOpen(false)} onDone={fetchActivities} />
      <ActivityDialog open={!!editActivity} onClose={() => setEditActivity(null)} onDone={fetchActivities} activity={editActivity} />
      <ActivityDetailDialog activity={detailActivity} open={!!detailActivity} onClose={() => setDetailActivity(null)} />
    </div>
  );
}


/* ====================================================================
   ENTITY OVERVIEW TAB (HR/Principal view)
   ==================================================================== */
// ========== FACULTY SCORES CARD (AICTE + UGC) ==========
function FacultyScoresCard({ contributors }) {
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!contributors || contributors.length === 0) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      try {
        const results = await Promise.all(
          contributors.slice(0, 10).map(async (tc) => {
            const [aicteRes, ugcRes] = await Promise.all([
              fetch(`${API_URL}/api/lnd/api-score/${tc.employee_id}`, { headers: h(), credentials: 'include' }),
              fetch(`${API_URL}/api/lnd/ugc-score/${tc.employee_id}`, { headers: h(), credentials: 'include' }),
            ]);
            const aicte = aicteRes.ok ? await aicteRes.json() : null;
            const ugc = ugcRes.ok ? await ugcRes.json() : null;
            return { ...tc, aicte, ugc };
          })
        );
        setScores(results);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [contributors]);

  if (loading) return <div className="h-32 bg-slate-100 rounded-lg animate-pulse" />;
  if (scores.length === 0) return null;

  return (
    <Card className="border-0 shadow-sm" data-testid="faculty-scores-card">
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <Award className="w-4 h-4 text-[#DC2626]" /> Faculty API Scores (AICTE + UGC)
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left p-3 text-xs uppercase tracking-wider font-semibold text-slate-500">Faculty</th>
                <th className="text-center p-3 text-xs uppercase tracking-wider font-semibold text-indigo-600">AICTE Total</th>
                <th className="text-center p-3 text-xs uppercase tracking-wider font-semibold text-slate-400">Research</th>
                <th className="text-center p-3 text-xs uppercase tracking-wider font-semibold text-slate-400">Teaching</th>
                <th className="text-center p-3 text-xs uppercase tracking-wider font-semibold text-slate-400">Service</th>
                <th className="text-center p-3 text-xs uppercase tracking-wider font-semibold text-emerald-600">UGC Total</th>
                <th className="text-center p-3 text-xs uppercase tracking-wider font-semibold text-slate-400">Teaching</th>
                <th className="text-center p-3 text-xs uppercase tracking-wider font-semibold text-slate-400">Prof Dev</th>
                <th className="text-center p-3 text-xs uppercase tracking-wider font-semibold text-slate-400">Research</th>
              </tr>
            </thead>
            <tbody>
              {scores.map(s => (
                <tr key={s.employee_id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors" data-testid={`score-row-${s.employee_id}`}>
                  <td className="p-3">
                    <p className="font-medium">{s.employee_name}</p>
                    <p className="text-xs text-slate-400">{s.department}</p>
                  </td>
                  <td className="p-3 text-center font-bold text-indigo-700 font-mono">{s.aicte?.total_api_score || 0}</td>
                  <td className="p-3 text-center text-slate-600 font-mono text-xs">{s.aicte?.category_1_research || 0}</td>
                  <td className="p-3 text-center text-slate-600 font-mono text-xs">{s.aicte?.category_2_teaching || 0}</td>
                  <td className="p-3 text-center text-slate-600 font-mono text-xs">{s.aicte?.category_3_service || 0}</td>
                  <td className="p-3 text-center font-bold text-emerald-700 font-mono">{s.ugc?.total_ugc_score || 0}</td>
                  <td className="p-3 text-center text-slate-600 font-mono text-xs">{s.ugc?.category_1_teaching || 0}</td>
                  <td className="p-3 text-center text-slate-600 font-mono text-xs">{s.ugc?.category_2_professional || 0}</td>
                  <td className="p-3 text-center text-slate-600 font-mono text-xs">{s.ugc?.category_3_research || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function EntityOverview({ entityId }) {
  const [summary, setSummary] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmp, setSelectedEmp] = useState('all');
  const [detailActivity, setDetailActivity] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear().toString());

  useEffect(() => { fetchData(); }, [entityId, year]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summRes, actRes] = await Promise.all([
        fetch(`${API_URL}/api/lnd/entity-summary?entity_id=${entityId || ''}&year=${year}`, { headers: h(), credentials: 'include' }),
        fetch(`${API_URL}/api/lnd/activities?entity_id=${entityId || ''}&year=${parseInt(year)}`, { headers: h(), credentials: 'include' })
      ]);
      if (summRes.ok) setSummary(await summRes.json());
      if (actRes.ok) setActivities(await actRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleVerify = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/lnd/activities/${id}/verify`, { method: 'PUT', headers: h(), credentials: 'include' });
      if (res.ok) { toast.success('Verified'); fetchData(); }
    } catch (e) { toast.error('Failed'); }
  };

  const handleReject = async (id) => {
    const reason = prompt('Rejection reason:');
    if (reason === null) return;
    try {
      const res = await fetch(`${API_URL}/api/lnd/activities/${id}/reject?reason=${encodeURIComponent(reason)}`, { method: 'PUT', headers: h(), credentials: 'include' });
      if (res.ok) { toast.success('Rejected'); fetchData(); }
    } catch (e) { toast.error('Failed'); }
  };

  const filtered = activities.filter(a => {
    const matchCat = filterCat === 'all' || a.category === filterCat;
    const matchStatus = filterStatus === 'all' || a.status === filterStatus;
    const matchEmp = selectedEmp === 'all' || a.employee_id === selectedEmp;
    const matchSearch = !searchTerm || a.title?.toLowerCase().includes(searchTerm.toLowerCase()) || a.employee_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchCat && matchStatus && matchEmp && matchSearch;
  });

  const uniqueEmps = [...new Map(activities.map(a => [a.employee_id, { id: a.employee_id, name: a.employee_name }])).values()];

  if (loading) return <div className="space-y-4">{[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-slate-100 rounded-lg animate-pulse" />)}</div>;

  return (
    <div className="space-y-6" data-testid="entity-overview-tab">
      {/* Summary Cards */}
      {summary && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Total Activities', value: summary.total_activities, icon: BookOpen, color: 'text-blue-600', bg: 'bg-blue-50' },
              { label: 'Contributing Faculty', value: summary.total_employees_with_activities, icon: Users, color: 'text-indigo-600', bg: 'bg-indigo-50' },
              { label: 'Verified', value: summary.verified, icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
              { label: 'Pending Review', value: summary.pending, icon: Clock, color: 'text-orange-600', bg: 'bg-orange-50' },
            ].map(s => (
              <Card key={s.label} className="border-0 shadow-sm">
                <CardContent className="p-3 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center`}><s.icon className={`w-5 h-5 ${s.color}`} /></div>
                  <div><p className="text-xs text-slate-500">{s.label}</p><p className="text-lg font-bold">{s.value}</p></div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Category + Department breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-sm">Category Breakdown</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(summary.category_breakdown || {}).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
                  <div key={cat} className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded flex items-center justify-center ${getCategoryColor(cat)}`}>{getCategoryIcon(cat)}</div>
                    <span className="text-sm flex-1 truncate">{cat}</span>
                    <span className="text-sm font-bold">{count}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4 text-amber-500" /> Top Contributors</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {(summary.top_contributors || []).slice(0, 8).map((tc, i) => (
                  <div key={tc.employee_id} className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-400 w-4">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{tc.employee_name}</p>
                      <p className="text-xs text-slate-500">{tc.department}</p>
                    </div>
                    <Badge variant="outline">{tc.total_activities}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Faculty API Scores (AICTE + UGC) */}
          <FacultyScoresCard contributors={summary.top_contributors || []} />
        </>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 flex-wrap">
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-28" data-testid="lnd-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[2026, 2025, 2024, 2023].map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search by title or employee..." className="pl-10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="entity-lnd-search" />
        </div>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-52" data-testid="entity-lnd-filter-cat"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-32" data-testid="entity-lnd-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="submitted">Pending</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Select value={selectedEmp} onValueChange={setSelectedEmp}>
          <SelectTrigger className="w-44" data-testid="entity-lnd-filter-emp"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Employees</SelectItem>
            {uniqueEmps.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Activity Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No activities found</TableCell></TableRow>
                ) : filtered.map(a => (
                  <TableRow key={a.activity_id} data-testid={`entity-row-${a.activity_id}`}>
                    <TableCell>
                      <p className="font-medium text-sm">{a.employee_name}</p>
                      <p className="text-xs text-slate-500">{a.department}</p>
                    </TableCell>
                    <TableCell>
                      <Badge className={getCategoryColor(a.category) + ' text-xs'}>{a.custom_category || a.category}</Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px]">
                      <p className="text-sm truncate">{a.title}</p>
                      {a.journal_name && <p className="text-xs text-slate-400">{a.journal_name}</p>}
                      {a.event_name && <p className="text-xs text-slate-400">{a.event_name}</p>}
                    </TableCell>
                    <TableCell className="text-sm">{a.date}</TableCell>
                    <TableCell>
                      <Badge className={a.status === 'verified' ? 'bg-green-100 text-green-700' : a.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}>
                        {a.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="icon" className="w-7 h-7" onClick={() => setDetailActivity(a)}><Eye className="w-3.5 h-3.5" /></Button>
                        {a.status === 'submitted' && (
                          <>
                            <Button variant="ghost" size="icon" className="w-7 h-7 text-green-600" onClick={() => handleVerify(a.activity_id)} data-testid={`verify-${a.activity_id}`}><CheckCircle className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="w-7 h-7 text-red-600" onClick={() => handleReject(a.activity_id)} data-testid={`reject-${a.activity_id}`}><XCircle className="w-3.5 h-3.5" /></Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <ActivityDetailDialog activity={detailActivity} open={!!detailActivity} onClose={() => setDetailActivity(null)} />
    </div>
  );
}


/* ====================================================================
   MAIN L&D PAGE
   ==================================================================== */
export default function LearningDevelopment() {
  const { selectedEntity } = useOutletContext() || {};
  const { user } = useAuth();
  const isPrivileged = ['super_admin', 'admin', 'hr', 'principal', 'hod'].includes(user?.role);
  const entityId = selectedEntity?.entity_id;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="lnd-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">Learning & Development</h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm">Track publications, conferences, FDPs, workshops, and professional growth activities</p>
      </div>

      <Tabs defaultValue="my-activities" className="w-full">
        <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${isPrivileged ? 2 : 1}, 1fr)` }}>
          <TabsTrigger value="my-activities" data-testid="tab-my-activities" className="flex items-center gap-1.5">
            <BookOpen className="w-4 h-4" />
            <span className="hidden sm:inline">My Activities</span>
            <span className="sm:hidden">My L&D</span>
          </TabsTrigger>
          {isPrivileged && (
            <TabsTrigger value="entity-overview" data-testid="tab-entity-overview" className="flex items-center gap-1.5">
              <Building className="w-4 h-4" />
              <span className="hidden sm:inline">Entity Overview</span>
              <span className="sm:hidden">Overview</span>
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="my-activities" className="mt-6">
          <MyActivities />
        </TabsContent>

        {isPrivileged && (
          <TabsContent value="entity-overview" className="mt-6">
            <EntityOverview entityId={entityId} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
